import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FieldSelectModule, FieldTextModule } from '@mri/app/modules';
import { AnyObject, Choice, Command, CommandConfig, CurrentMobile, FieldOption, ResponseSimple } from '@mri/types';
import { map, mergeMap, Observable, Subject, Subscription, tap } from 'rxjs';
import { GenericResourceService } from '@mri/app/resources';
import { ChoiceTypes } from '@mri/enums';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-send-command',
  standalone: true,
  imports: [
    FieldSelectModule,
    MatSlideToggleModule,
    MatProgressBarModule,
    MatButtonModule,
    FieldTextModule
  ],
  templateUrl: './send-command.component.html',
  styleUrl: './send-command.component.scss'
})
export class SendCommandComponent implements OnInit, OnDestroy {
  private _subscription = new Subscription();
  private _elementId$ = new Subject<string>();
  private _commandConfigs?: CommandConfig[];
  private _commandChoices?: Choice[];
  private _submit$ = new Subject<Command>();

  channelList = ['tcp', 'sms'];
  element?: CurrentMobile;
  options: FieldOption[] = [];
  commandKey?: string;
  channelAvailableKeys: string[] = [];
  channelToSent = new Set<string>();
  isLoading: boolean;
  isSubmit: boolean;
  customText: string = '';

  @Input() elementId: string;

  @Output() submitted = new EventEmitter<AnyObject>();
  @Output() cancelled = new EventEmitter<AnyObject>();

  constructor(
    private _genericSrv: GenericResourceService
  ) {
  }

  ngOnInit() {
    this._subscription.add(
      this._elementId$.pipe(
        tap(() => this.isLoading = true),
        mergeMap(() => this._obtainCurrentMobile()),
        tap(() => this.isLoading = false)
      ).subscribe()
    );

    this._subscription.add(
      this._brigCommands()
        .subscribe()
    );

    this._subscription.add(
      this._submit$.pipe(
        tap(() => this.isLoading = true),
        tap(() => this.isSubmit = true),
        mergeMap(command => this._genericSrv.postElement<ResponseSimple<Command>>('commands', command)),
        tap(() => this.isLoading = false),
        tap(() => this.isSubmit = false),
        tap(response => this.submitted.next(response.object))
      ).subscribe()
    );

    if (this.elementId) this._elementId$.next(this.elementId);
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  onChangeMobile(id: string) {
    this.elementId = id;
    this._elementId$.next(id);
    this.channelToSent.clear();
    this.commandKey = undefined;
  }

  onChangeCommand(value: string) {
    this.commandKey = value;
    const config: CommandConfig | undefined = value !== 'personalizeCommand' ?
      this._commandConfigs?.find(co => co.key === value) :
      { key: 'personalizeCommand', channels: [{ key: 'sms', enable: true }, { key: 'tcp', enable: true }] }
    ;

    this.channelAvailableKeys = (config?.channels || [])
      .filter(channel => channel.enable)
      .map(channel => channel.key) || [];

    this.channelToSent.clear();

    this.channelToSent.add(this.channelAvailableKeys[0]);

  }

  toggleChannel(value: string) {
    if (this.channelToSent.has(value)) {
      this.channelToSent.delete(value);
    } else {
      this.channelToSent.add(value);
    }
  }

  onSubmit() {
    if (!this.commandKey) return;
    if (!this.element?.deviceId) return;

    this._submit$.next({
      commandKey: this.commandKey,
      deviceId: this.element?.deviceId,
      mobileId: this.element?.mobile?._id,
      channelKeys: [...this.channelToSent],
      customText: this.customText
    });
  }

  onCancel() {
    this.cancelled.next(null);
  }


  private _obtainCurrentMobile(): Observable<CurrentMobile> {
    return this._genericSrv
      .getElement<CurrentMobile>('mobiles/currents', this.elementId)
      .pipe(
        map(response => response.object),
        tap(element => this.element = element),
        tap(() => this._generateOptions())
      );
  }


  private _brigCommands(): Observable<Array<Choice>> {
    return this._genericSrv
      .getElements<Choice>(
        'choices',
        { paginate: { page: 1, size: 99 }, filter: { fieldKey: ChoiceTypes.COMMAND } }
      )
      .pipe(
        map(({ objects }) => objects),
        tap(commands => this._commandChoices = commands)
      );
  }

  private _generateOptions() {
    if (!this.element) return;

    this._commandConfigs = this.element.hardware?.config?.commands || [];

    const commandPermitKeys = this._commandConfigs
      .filter(c => c.channels.reduce((acc, channel) => acc || channel.enable, false))
      .map(c => c.key);

    this.options = (this._commandChoices || [])
      .filter(c => [...commandPermitKeys, 'personalizeCommand'].includes(c.value))
      .map(c => {
        const config = this._commandConfigs?.find(co => co.key === c.value);

        const badges = config?.channels?.filter(channel => channel.enable)?.map(channel => channel.key) || [];
        return ({
          label: c.label?.['es'] || c.label?.['default'] || '',
          value: c.value,
          image: c.image,
          icon: c.icon,
          badges: badges
        });
      });
  }


}
